import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Package, MinusCircle, PlusCircle } from 'lucide-react';

interface ReceiveItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemUnit: string;
  totalQuantity: number;
  remainingQuantity: number;
  onReceive: (quantity: number) => void;
}

export default function ReceiveItemModal({
  isOpen,
  onClose,
  itemName,
  itemUnit,
  totalQuantity,
  remainingQuantity,
  onReceive
}: ReceiveItemModalProps) {
  const [quantity, setQuantity] = useState(remainingQuantity);
  const [isAnimating, setIsAnimating] = useState(false);

  // Initialize quantity when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setQuantity(remainingQuantity);
    }
  }, [isOpen, remainingQuantity]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleIncrement = () => {
    if (quantity < remainingQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onReceive(quantity);
    onClose();
  };
  
  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-50 flex items-center justify-center`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'scale-100' : 'scale-95'
        } w-full max-w-md overflow-hidden`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-4 py-3 bg-gray-900 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">Receive Items</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-300"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">{itemName}</h3>
              </div>
              <p className="text-sm text-gray-600">
                Select how many {itemUnit}s you want to receive out of {remainingQuantity} remaining
              </p>
            </div>

            <div className="flex items-center justify-center border border-gray-200 rounded-lg p-4 mb-6">
              <button 
                type="button"
                onClick={handleDecrement}
                disabled={quantity <= 1}
                className="p-2 text-blue-600 disabled:text-gray-300"
              >
                <MinusCircle className="h-6 w-6" />
              </button>
              <div className="mx-4 text-center">
                <div className="text-3xl font-bold text-gray-900">{quantity}</div>
                <div className="text-sm text-gray-500">of {remainingQuantity} {remainingQuantity > 1 ? itemUnit + 's' : itemUnit}</div>
              </div>
              <button 
                type="button"
                onClick={handleIncrement}
                disabled={quantity >= remainingQuantity}
                className="p-2 text-blue-600 disabled:text-gray-300"
              >
                <PlusCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total quantity in order:</span>
                <span className="font-medium text-gray-900">{totalQuantity} {totalQuantity > 1 ? itemUnit + 's' : itemUnit}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Already received:</span>
                <span className="font-medium text-gray-900">{totalQuantity - remainingQuantity} {(totalQuantity - remainingQuantity) > 1 ? itemUnit + 's' : itemUnit}</span>
              </div>
              <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                <span className="text-gray-500">Receiving now:</span>
                <span className="font-medium text-blue-600">{quantity} {quantity > 1 ? itemUnit + 's' : itemUnit}</span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Receive Items
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 